import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import PageWrapper from 'components/layout/PageWrapper';
import 'style.css';

interface PostMeta {
    slug: string;
    title: string;
    date: string;
    excerpt: string;
    coverImage: string;
}

const formatDate = (iso: string) =>
    new Date(iso + 'T12:00:00').toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
    });

const BlogPost = () => {
    const { slug } = useParams<{ slug: string }>();
    const [meta, setMeta] = useState<PostMeta | null>(null);
    const [content, setContent] = useState('');
    const [metaNotFound, setMetaNotFound] = useState(false);
    const [contentMissing, setContentMissing] = useState(false);

    useEffect(() => {
        if (!slug) return;
        fetch('/blog/posts.json')
            .then(r => r.text())
            .then(text => JSON.parse(text.replace(/,\s*([}\]])/g, '$1')))
            .then((posts: PostMeta[]) => {
                const found = posts.find(p => p.slug === slug);
                if (found) setMeta(found); else setMetaNotFound(true);
            });

        fetch(`/blog/${slug}.md`)
            .then(r => {
                const ct = r.headers.get('content-type') || '';
                if (!r.ok || ct.includes('text/html')) { setContentMissing(true); return ''; }
                return r.text();
            })
            .then(text => { if (text) setContent(text); });
    }, [slug]);

    if (metaNotFound) {
        return (
            <div className="blog-post-page">
                <PageWrapper>
                    <Link to="/blog" className="fleet-back-link">← Back to Updates</Link>
                    <p style={{ color: 'var(--color-text-muted)', marginTop: '2rem' }}>Post not found.</p>
                </PageWrapper>
            </div>
        );
    }

    return (
        <div className="blog-post-page">
            <PageWrapper>
                <Link to="/blog" className="fleet-back-link">← Back to Updates</Link>
                {meta && (
                    <div className="blog-post-header">
                        <h1 className="blog-post-title">{meta.title}</h1>
                        <p className="blog-post-date">{formatDate(meta.date)}</p>
                        <img
                            src={meta.coverImage}
                            alt={meta.title}
                            className="blog-post-cover"
                            loading="lazy"
                        />
                    </div>
                )}
                <div className="blog-post-body">
                    {contentMissing
                        ? <p className="blog-post-coming-soon">Content coming soon.</p>
                        : <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
                    }
                </div>
            </PageWrapper>
        </div>
    );
};

export default BlogPost;
